import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import { Alert, Button, Paper, Snackbar, Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';

import { getIntlLocale } from '@/shared/i18n';
import { usePermissions } from '@/shared/permissions';
import { useSession } from '@/shared/session';
import { ErrorState, LoadingState, PageContainer } from '@/shared/ui';
import { formatDayTime, formatDurationShort, VersionConflictAlert } from '@/shared/ui/conversation';

import { OPERATOR_QUEUE_PARAMS } from '../../api/conversationsApi';
import {
  conversationsKeys,
  mergedAliasTarget,
  useConversation,
  useConversationEpisodes,
} from '../../hooks';
import { staffLabel } from '../../model/staff';
import type { ConversationMessageView, ConversationPage } from '../../model/types';
import {
  ContextSummaryCard,
  CustomerCard,
  EpisodesPanel,
  EscalationPanel,
  SessionsCard,
} from '../components/AsideCards';
import { ConversationHeader } from '../components/ConversationHeader';
import { OperatorComposer } from '../components/OperatorComposer';
import { Timeline } from '../components/Timeline';

type FeedbackSeverity = 'success' | 'error' | 'info' | 'warning';

interface Banner {
  key: string;
  node: ReactNode;
}

/** `/conversations/:conversationId` — the cross-channel conversation view (design §7). */
export function ConversationDetailPage() {
  const { t } = useTranslation('conversations');
  const { conversationId: routeId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const { user } = useSession();
  const locale = getIntlLocale();

  const canView = hasPermission('conversations.view');
  const canManage = canView && hasPermission('conversations.manage');
  const canReply = canManage && hasPermission('conversations.feature.operator-reply');
  const canViewPii = hasPermission('customers.feature.pii-view');
  const canViewCustomer = hasPermission('customers.view');
  const canViewChatbotSettings = hasPermission('settings.chatbot.view');
  const canViewTemplates = hasPermission('content.view');

  const [feedback, setFeedback] = useState<{ severity: FeedbackSeverity; message: string } | null>(
    null,
  );
  const notify = useCallback(
    (severity: FeedbackSeverity, message: string) => setFeedback({ severity, message }),
    [],
  );

  const [conflictedVersion, setConflictedVersion] = useState<number | null>(null);
  const [reloading, setReloading] = useState(false);
  const [replyTo, setReplyTo] = useState<ConversationMessageView | null>(null);

  const detailQuery = useConversation(canView ? routeId : null);
  const detail = detailQuery.data;
  const effectiveId = detail?.id ?? routeId;

  // MERGED alias (design §11.8, adr-merchant-portal-021 D2): the requested id resolved to a survivor.
  useEffect(() => {
    if (!detail) return;
    const target = mergedAliasTarget(routeId, detail);
    if (target) {
      queryClient.setQueryData(conversationsKeys.detail(target), detail);
      navigate(`/conversations/${target}`, { replace: true, state: location.state as unknown });
      notify('info', t('detail.merged.redirected'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reacts to the fetched detail identity
  }, [detail, routeId]);

  // A version conflict clears itself once a poll shows the state has genuinely moved on (§11.2).
  useEffect(() => {
    if (conflictedVersion == null) return;
    if (detail?.currentEpisode?.version !== conflictedVersion) {
      setConflictedVersion(null);
    }
  }, [conflictedVersion, detail?.currentEpisode?.version]);

  const episodesQuery = useConversationEpisodes(canView ? effectiveId : null, { size: 200 });
  const episodesById = useMemo(
    () => new Map((episodesQuery.data?.items ?? []).map((e) => [e.episodeId, e])),
    [episodesQuery.data],
  );

  const cachedQueue = queryClient.getQueryData<ConversationPage>(
    conversationsKeys.list(OPERATOR_QUEUE_PARAMS),
  );
  const nextWaitingId = useMemo(() => {
    if (!cachedQueue?.content?.length) return null;
    const currentIndex = cachedQueue.content.findIndex((row) => row.id === effectiveId);
    const next = currentIndex >= 0 ? cachedQueue.content[currentIndex + 1] : cachedQueue.content[0];
    return next && next.id !== effectiveId ? next.id : null;
  }, [cachedQueue, effectiveId]);

  async function reload() {
    setReloading(true);
    try {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: conversationsKeys.detail(effectiveId) }),
        queryClient.invalidateQueries({
          queryKey: [...conversationsKeys.root, 'episodes', effectiveId],
        }),
        queryClient.invalidateQueries({ queryKey: conversationsKeys.messagesLatest(effectiveId) }),
        queryClient.invalidateQueries({
          queryKey: [...conversationsKeys.root, 'messages', effectiveId],
        }),
      ]);
    } finally {
      setReloading(false);
    }
  }

  const backAction = (
    <Button
      key="back"
      component={RouterLink}
      to="/conversations"
      state={location.state as unknown}
      color="inherit"
    >
      {t('detail.back')}
    </Button>
  );

  if (!canView) {
    return (
      <PageContainer maxWidth={1200} actions={backAction}>
        <ErrorState title={t('detail.notFound.title')} message={t('detail.loadError')} />
      </PageContainer>
    );
  }

  if (detailQuery.isLoading) {
    return (
      <PageContainer maxWidth={1200} actions={backAction}>
        <LoadingState label={t('detail.loading')} />
      </PageContainer>
    );
  }

  if (detailQuery.isError || !detail) {
    const notFound = detailQuery.error?.status === 404;
    return (
      <PageContainer maxWidth={1200} actions={backAction}>
        <ErrorState
          title={notFound ? t('detail.notFound.title') : t('detail.loadError')}
          message={notFound ? t('detail.notFound.message') : detailQuery.error?.message}
          onRetry={notFound ? undefined : () => void detailQuery.refetch()}
        />
      </PageContainer>
    );
  }

  // Still resolving a MERGED alias — one extra render before history.replace takes effect.
  if (detail.id !== routeId) {
    return (
      <PageContainer maxWidth={1200} actions={backAction}>
        <LoadingState label={t('detail.loading')} />
      </PageContainer>
    );
  }

  const episode = detail.currentEpisode ?? null;
  const unreachableSession = detail.sessions.find((s) => !s.reachable);
  const reachableSession = detail.sessions.find((s) => s.reachable);
  // "You" is decided by `ackedByRef.actorId === user.id` ALONE — the retired comparison with
  // `user.name`/`user.email` is gone (design §13.2).
  const acker = staffLabel(
    detail.openEscalation?.ackedByRef,
    detail.openEscalation?.ackedBy,
    user?.id ?? null,
    t,
  );

  // Banner priority order (design §7.2): slot 0 (conflict) and slot P (PII) are separate, uncounted
  // slots rendered above; at most TWO of the ranked banners below are shown.
  const rankedBanners: Banner[] = [];
  if (unreachableSession) {
    rankedBanners.push({
      key: 'unreachable',
      node: (
        <Alert key="unreachable" severity="error" icon={<BlockRoundedIcon fontSize="inherit" />}>
          {t('detail.banners.unreachable', {
            channel: t(`channel.${unreachableSession.channel}`, unreachableSession.channel),
          })}
          {reachableSession
            ? ` ${t('detail.banners.unreachableAlt', { channel: t(`channel.${reachableSession.channel}`, reachableSession.channel) })}`
            : ` ${t('detail.banners.unreachableNone')}`}
        </Alert>
      ),
    });
  }
  if (episode?.status === 'WAITING_OPERATOR' && detail.openEscalation) {
    const waitingMs = Date.now() - new Date(detail.openEscalation.requestedAt).getTime();
    rankedBanners.push({
      key: 'waiting',
      node: (
        <Alert key="waiting" severity="info" icon={<HourglassTopRoundedIcon fontSize="inherit" />}>
          {t('detail.banners.waiting', {
            time: formatDayTime(detail.openEscalation.requestedAt, locale),
            duration: formatDurationShort(Math.max(0, waitingMs), locale),
          })}
        </Alert>
      ),
    });
  }
  if (episode?.status === 'OPERATOR') {
    rankedBanners.push({
      key: 'operator',
      node: (
        <Alert key="operator" severity="info" icon={<SmartToyRoundedIcon fontSize="inherit" />}>
          {acker?.kind === 'you'
            ? t('detail.banners.operatorYou')
            : acker?.kind === 'named'
              ? t('detail.banners.operatorOther', { name: acker.label })
              : acker?.kind === 'formerEmployee'
                ? t('detail.banners.operatorFormer')
                : t('detail.banners.operatorUnknown')}
        </Alert>
      ),
    });
  }
  if (detail.status === 'CLOSED' && !episode) {
    const newest = episodesQuery.data?.items[0];
    // OPERATOR_CLOSED with a resolvable staff actor names them (design §13.2); every other reason
    // (or an operator-closed episode with no ref/id at all — an older BFF) keeps the reason sentence.
    const closer =
      newest?.closedReason === 'OPERATOR_CLOSED'
        ? staffLabel(newest.closedByRef, newest.closedBy, user?.id ?? null, t)
        : null;
    rankedBanners.push({
      key: 'closed',
      node: (
        <Alert key="closed" severity="info">
          {newest?.closedAt && closer
            ? t('detail.banners.closedBy', {
                date: formatDayTime(newest.closedAt, locale),
                name: closer.label,
              })
            : newest?.closedAt
              ? t('detail.banners.closed', {
                  date: formatDayTime(newest.closedAt, locale),
                  reason: t(
                    `episodes.closedReasons.${newest.closedReason}`,
                    newest.closedReason ?? '',
                  ),
                })
              : t('detail.banners.closedNoDetails')}
        </Alert>
      ),
    });
  }

  return (
    <PageContainer
      maxWidth={1200}
      actions={
        <Stack direction="row" spacing={1}>
          {backAction}
          {nextWaitingId ? (
            <Button component={RouterLink} to={`/conversations/${nextWaitingId}`} color="inherit">
              {t('detail.nextWaiting')}
            </Button>
          ) : null}
        </Stack>
      }
    >
      <Stack spacing={1.5}>
        <ConversationHeader
          detail={detail}
          canManage={canManage}
          canViewCustomer={canViewCustomer}
          canViewPii={canViewPii}
          conflictActive={conflictedVersion != null}
          onVersionConflict={(v) => setConflictedVersion(v)}
          onActionSuccess={() => void reload()}
          notify={notify}
        />

        {conflictedVersion != null ? (
          <VersionConflictAlert onReload={() => void reload()} reloading={reloading} />
        ) : null}
        {!canViewPii ? (
          <Alert
            severity="info"
            icon={<VisibilityOffRoundedIcon fontSize="inherit" />}
            role="status"
            sx={{ py: 0 }}
          >
            {t('pii.pageNotice')}
          </Alert>
        ) : null}
        {rankedBanners.slice(0, 2).map((banner) => banner.node)}

        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ flex: 1, minHeight: 0 }}>
          <Stack sx={{ flex: 1, minWidth: 0 }} spacing={1.5}>
            <Paper variant="outlined" sx={{ flex: 1, minHeight: 400, display: 'flex' }}>
              <Timeline
                conversationId={effectiveId}
                currentEpisodeId={episode?.episodeId}
                episodesById={episodesById}
                canViewPii={canViewPii}
                canReply={canReply}
                onReplyToThis={setReplyTo}
                meId={user?.id}
              />
            </Paper>
            {canReply && episode ? (
              // Design §7.5/Q6 (confirmed): hidden on a CLOSED thread — `currentEpisode` is null then,
              // and a reply there would reopen it, which is a campaign/proactive-outreach concern
              // (adr-conversation-007), not an operator reply.
              <OperatorComposer
                conversationId={effectiveId}
                sessions={detail.sessions}
                lastChannel={detail.lastChannel}
                replyTo={replyTo}
                onClearReplyTo={() => setReplyTo(null)}
                canViewPii={canViewPii}
                me={user ? { id: user.id, name: user.name } : null}
                notify={notify}
              />
            ) : null}
          </Stack>

          <Stack sx={{ width: { xs: '100%', lg: 360 }, flexShrink: 0 }} spacing={1.5}>
            <EscalationPanel
              detail={detail}
              canViewChatbotSettings={canViewChatbotSettings}
              canViewTemplates={canViewTemplates}
              meId={user?.id}
            />
            <ContextSummaryCard detail={detail} />
            <EpisodesPanel
              conversationId={effectiveId}
              currentEpisodeId={episode?.episodeId}
              canManage={canManage}
              conflictActive={conflictedVersion != null}
              onVersionConflict={(v) => setConflictedVersion(v)}
              notify={notify}
              meId={user?.id}
            />
            <CustomerCard detail={detail} canViewCustomer={canViewCustomer} />
            <SessionsCard detail={detail} />
          </Stack>
        </Stack>
      </Stack>

      <Snackbar
        open={feedback != null}
        autoHideDuration={5000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {feedback ? (
          <Alert
            severity={feedback.severity}
            onClose={() => setFeedback(null)}
            sx={{ width: '100%' }}
          >
            {feedback.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </PageContainer>
  );
}
